// Accounting › Payroll (Blueprint's; pages/ux-full accounting/payroll), its own module out of Books.tsx
// (BL-189): PageHead, the pay period to look at (a SelectField: it shows the newest six) and Open the next
// period, then the period, a Section whose state says where it stands (Open, Hours locked, Sent to the
// provider, Posted). Its hours by type are a DataTable with the total as its foot; individual pay shows
// when the server gives it, to whoever holds payroll.view (QA-266). What stops the lock is a red line
// each; a missing start date is a Checkbox (BL-014). The provider's money is a FormDialog each: what it
// took from the bank (payroll.recordFunding) and a CRA payment (payroll.recordRemittance), their fields
// and checks as before; posting the provider's register asks in a ConfirmDialog. Commissions and what's
// still to send CRA are RowLists. The timesheets are Hours' (one Timesheet). Riverstone keeps Payroll.tsx
// and PayrollSetup.tsx; payroll-page.css lays out the lines between the parts, tokens only.
import {
  Button,
  Checkbox,
  Choice,
  ChoiceGroup,
  ConfirmDialog,
  DataTable,
  FormDialog,
  Link,
  Notice,
  RowList,
  SelectField,
  Skeleton,
  SkeletonGroup,
  TextField,
  type DataColumn,
  type MarkState,
} from '@flowology/ui-web/fl';
import { PageHead, Section } from '@flowology/ui-web/patterns';
import { useState } from 'react';
import { client } from '../api.ts';
import { PageBanners, usePageCrumb, type NavProps } from '../Nav.tsx';
import { useSaveKey } from '../shared/SaveButton.tsx';
import { dayText, hours, money, parseMoney, todayIn } from './model.ts';
import { BOOKS_PARTS } from './parts.ts';
import {
  COMMISSION_RULES,
  commissionsByPerson,
  fundingWords,
  nextPeriod,
  percentToRate,
} from './payroll-model.ts';
import type { BankAccount, PayPeriod, PayPeriodSummary } from './types.ts';
import { errorText, useAct, useQuery } from './ui.tsx';
import './payroll-page.css';

const PART = BOOKS_PARTS.find((p) => p.part === 'payroll')!;

/** How payroll.prepare's "no start date" flags start (packages/modules payroll/leave.ts). */
const NO_START_DATE = 'No start date:';

/** The pay periods to pick from: the newest six, as the period buttons showed them. */
const SHOWN = 6;

const STATE: Record<PayPeriodSummary['status'], { mark: MarkState; word: string }> = {
  open: { mark: 'working', word: 'Open' },
  locked: { mark: 'waiting', word: 'Hours locked' },
  sent: { mark: 'waiting', word: 'Sent to the provider' },
  posted: { mark: 'done', word: 'Posted' },
};

interface Staff {
  id: string;
  displayName: string;
  isTechnician: boolean;
}

type Line = PayPeriod['lines'][number];

const cad = (cents: number) =>
  new Intl.NumberFormat('en-CA', { style: 'currency', currency: 'CAD' }).format(cents / 100);

const dates = (p: Pick<PayPeriodSummary, 'startDate' | 'endDate'>) =>
  `${dayText(p.startDate)} – ${dayText(p.endDate)}`;

/** A part still loading (skeleton rows), or one that didn't load (a red sentence and Try again). */
function Waiting({ error, what, onRetry }: { error: string | null; what: string; onRetry: () => void }) {
  return error ? (
    <p className="bp-pay-bad" role="alert">
      <span>{error}</span>
      <Link onPress={onRetry}>Try again</Link>
    </p>
  ) : (
    <SkeletonGroup label={`Loading ${what}`}>
      {[0, 1, 2].map((i) => (
        <Skeleton key={i} shape="row" width="full" />
      ))}
    </SkeletonGroup>
  );
}

// ── open a pay period ──

function OpenPeriod({
  newest,
  timeZone,
  toast,
  onOpened,
  quiet,
}: {
  newest: PayPeriodSummary | null;
  timeZone?: string;
  toast: (m: string) => void;
  onOpened: (id: string) => void;
  /** Beside an existing period: Open the next period, a Link, until it's pressed. */
  quiet?: boolean;
}) {
  const week = useQuery<{ value: unknown }>('settings.get', { key: 'payroll.weekStartsOn' });
  const [open, setOpen] = useState(!quiet);
  const weekStartsOn = typeof week.data?.value === 'number' ? week.data.value : 0;
  const first = nextPeriod(newest, todayIn(timeZone), weekStartsOn);
  const [picked, setPicked] = useState<typeof first | null>(null);
  const d = picked ?? first;
  const act = useAct(toast, () => undefined);
  const ok =
    !!d.startDate && !!d.endDate && !!d.payDate && d.startDate <= d.endDate && d.payDate >= d.endDate;
  if (!open) return <Link onPress={() => setOpen(true)}>Open the next period</Link>;
  const field = (k: keyof typeof first, label: string) => (
    <TextField
      type="date"
      width="date"
      label={label}
      value={d[k]}
      onChange={(v) => setPicked({ ...d, [k]: v })}
    />
  );
  return (
    <Section title={newest ? 'The next pay period' : 'Open the first pay period'}>
      {!newest && (
        <p className="bp-pay-say">
          Hours come into a period from the technicians’ time entries; Prepare works them out at each person’s
          rate.
        </p>
      )}
      <form
        className="bp-pay-dates"
        aria-label="A pay period"
        onSubmit={(e) => {
          e.preventDefault();
          if (!ok || act.busy) return;
          void act
            .run<{ id: string }>(
              'payPeriods.create',
              d,
              `The period ${dayText(d.startDate)} to ${dayText(d.endDate)} is open.`,
            )
            .then((r) => {
              if (r) {
                setPicked(null);
                if (quiet) setOpen(false);
                onOpened(r.id);
              }
            });
        }}
      >
        {field('startDate', 'From')}
        {field('endDate', 'To')}
        {field('payDate', 'Paid on')}
        <div className="bp-pay-acts">
          <Button variant="primary" type="submit" isDisabled={!ok || act.busy}>
            {act.busy ? 'Opening…' : 'Open the period'}
          </Button>
          {quiet && (
            <Link onPress={() => setOpen(false)} isDisabled={act.busy}>
              Cancel
            </Link>
          )}
        </div>
      </form>
    </Section>
  );
}

// ── a period's hours ──

/** A row of the hours table: a person's line, or (the foot) the period's totals. */
interface HoursRow {
  id: string;
  line?: Line;
}

function hoursColumns(d: PayPeriod, seesPay: boolean): DataColumn<HoursRow>[] {
  const t = d.totals;
  const pay = (c: number | undefined) => (c === undefined ? '—' : money(c));
  const each = (
    id: string,
    label: string,
    of: (l: Line) => number,
    total?: number,
  ): DataColumn<HoursRow> => ({
    id,
    label,
    align: 'end',
    cell: (r) => ({ text: r.line ? hours(of(r.line)) : total === undefined ? '' : hours(total) }),
  });
  return [
    {
      id: 'person',
      label: 'Person',
      rowHeader: true,
      // a line with something to fix before the lock is red (DESIGN.md §2), its flags said under the table
      cell: (r) =>
        r.line
          ? { text: r.line.name, ...(r.line.flags.length ? { look: 'bad' as const } : {}) }
          : { text: 'Total' },
    },
    each('work', 'Work', (l) => l.workMinutes),
    each('drive', 'Drive', (l) => l.driveMinutes),
    each('shop', 'Shop', (l) => l.nonJobMinutes),
    each('regular', 'Regular', (l) => l.regularMinutes, t.regularMinutes),
    each('ot', '1.5×', (l) => l.overtimeMinutes, t.overtimeMinutes),
    each('double', '2×', (l) => l.doubleMinutes, t.doubleMinutes),
    each('stat', 'Stat worked', (l) => l.statMinutes + l.statDoubleMinutes),
    ...(seesPay
      ? ([
          {
            id: 'leave',
            label: 'Stat & sick',
            align: 'end',
            cell: (r) => ({ text: r.line ? pay(r.line.leaveCents) : '' }),
          },
          {
            id: 'commission',
            label: 'Commission',
            align: 'end',
            cell: (r) => ({
              text: r.line
                ? pay(
                    r.line.commissionsCents === undefined
                      ? undefined
                      : r.line.commissionsCents + (r.line.spiffsCents ?? 0),
                  )
                : money(t.commissionsCents),
            }),
          },
          {
            id: 'gross',
            label: 'Gross',
            align: 'end',
            cell: (r) => ({ text: r.line ? pay(r.line.grossCents) : money(t.grossCents) }),
          },
        ] satisfies DataColumn<HoursRow>[])
      : []),
  ];
}

/** Each stat and sick day: the average day's pay it pays, or why nothing (its note). */
function Leave({ lines }: { lines: readonly Line[] }) {
  if (!lines.some((l) => l.leave.length)) return null;
  return (
    <>
      <h3 className="bp-pay-sub">Statutory holidays and sick days</h3>
      <ul className="bp-pay-leave" aria-label="Statutory holidays and sick days">
        {lines.flatMap((l) =>
          l.leave.map((x) => (
            <li key={`${l.staffId}-${x.date}-${x.kind}`}>
              {l.name}, {dayText(x.date)}: {x.kind === 'stat' ? 'statutory holiday' : 'sick'}
              {x.amountCents
                ? ` · ${money(x.amountCents)}${
                    x.averageDayCents && x.averageDayCents !== x.amountCents
                      ? ` of an average day of ${money(x.averageDayCents)}`
                      : ' (an average day)'
                  }`
                : ''}
              {x.note ? ` · ${x.note}` : ''}
            </li>
          )),
        )}
      </ul>
    </>
  );
}

function PeriodView({
  id,
  summary,
  owner,
  timeZone,
  toast,
  onFunded,
}: {
  id: string;
  /** Its dates while it loads, from the list. */
  summary: PayPeriodSummary | null;
  owner: boolean;
  timeZone?: string;
  toast: (m: string) => void;
  /** A funding the company remits for adds to what's still to send CRA. */
  onFunded: () => void;
}) {
  const p = useQuery<PayPeriod>('payPeriods.get', { id });
  // A commissions lock or a funding changes the period's totals: it reads again (gap #12).
  const [version, setVersion] = useState(0);
  // a missing start date doesn't hold up the lock: the office confirms it by name (BL-014)
  const [withoutLeave, setWithoutLeave] = useState(false);
  const act = useAct(toast, () => {
    setVersion((v) => v + 1);
    return p.reload();
  });
  if (!p.data)
    return (
      <Section title={summary ? dates(summary) : 'Pay period'}>
        <Waiting error={p.error} what="the pay period" onRetry={() => void p.reload()} />
      </Section>
    );
  const d = p.data;
  const stale = !!d.stale;
  const flags = d.lines.flatMap((l) => l.flags.map((f) => `${l.name}: ${f}`));
  const noStart = d.lines.filter((l) => l.flags.some((f) => f.startsWith(NO_START_DATE)));
  const blocking = d.lines.some((l) => l.flags.some((f) => !f.startsWith(NO_START_DATE)));
  // Individual pay comes only to payroll.view holders; the totals come to everyone here.
  const seesPay = d.lines.some((l) => l.grossCents !== undefined);
  const exportFile = async () => {
    try {
      const f = await client.call<{ fileName: string; csv: string }>('payroll.exportFile', { periodId: id });
      const url = URL.createObjectURL(new Blob([f.csv], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = f.fileName;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      toast(errorText(e));
    }
  };
  return (
    <>
      <Section title={dates(d)} state={STATE[d.status]}>
        <div className="bp-pay-period">
          <p className="bp-pay-say">Pays {dayText(d.payDate)}</p>
          {stale && (
            <div role="status">
              <Notice title="Hours changed since this was prepared (a correction in the timesheet). Prepare it again before locking." />
            </div>
          )}
          <DataTable
            label="Hours by type"
            columns={hoursColumns(d, seesPay)}
            rows={d.lines.map((l) => ({ id: l.staffId, line: l }))}
            foot={d.lines.length ? { id: 'total' } : null}
            empty={
              d.status === 'open'
                ? 'No hours yet. Prepare reads the period’s time entries.'
                : 'No one was paid hours in this period.'
            }
          />
          <Leave lines={d.lines} />
          {!seesPay && d.lines.length > 0 && (
            <p className="bp-pay-small">
              This period: commissions <b>{money(d.totals.commissionsCents)}</b> · total pay{' '}
              <b>{money(d.totals.grossCents)}</b>. Each person’s pay shows to the owner, or to whoever the
              owner gave payroll to.
            </p>
          )}
          {flags.length > 0 && (
            <ul className="bp-pay-flags" aria-label="To fix before the lock">
              {flags.map((f) => (
                <li key={f}>
                  <span aria-hidden="true">!</span>
                  <span>{f}</span>
                </li>
              ))}
            </ul>
          )}
          {d.status === 'open' && !blocking && noStart.length > 0 && (
            <Checkbox isSelected={withoutLeave} onChange={setWithoutLeave}>
              Lock without stat and sick pay for: {noStart.map((l) => `${l.name} (no start date)`).join(', ')}
              . Once a start date is set, the next open period pays what they’re owed.
            </Checkbox>
          )}
          <div className="bp-pay-acts">
            {d.status === 'open' && (
              <>
                <Link
                  isDisabled={act.busy}
                  onPress={() =>
                    void act.run('payroll.prepare', { periodId: id }, 'Prepared from the time entries.')
                  }
                >
                  Prepare
                </Link>
                <Button
                  variant="primary"
                  // a stale period is refused until it's prepared again (QA-400)
                  isDisabled={
                    act.busy ||
                    blocking ||
                    (noStart.length > 0 && !withoutLeave) ||
                    d.lines.length === 0 ||
                    stale
                  }
                  onPress={() =>
                    void act.run(
                      'payroll.lockHours',
                      {
                        periodId: id,
                        ...(noStart.length ? { withoutStatAndSickFor: noStart.map((l) => l.staffId) } : {}),
                      },
                      'Hours locked.',
                    )
                  }
                >
                  Lock hours
                </Button>
              </>
            )}
            {d.status === 'locked' &&
              (owner || seesPay ? (
                <>
                  {seesPay && <Link onPress={() => void exportFile()}>Download the provider file</Link>}
                  {owner && (
                    <Button
                      variant="primary"
                      isDisabled={act.busy}
                      onPress={() =>
                        void act.run(
                          'payroll.sendToProvider',
                          { periodId: id },
                          'Sent to the payroll provider.',
                        )
                      }
                    >
                      Send to the provider
                    </Button>
                  )}
                </>
              ) : (
                <span className="bp-pay-note">Locked: the owner reviews and sends it (Q42).</span>
              ))}
            {/* sent: the provider's pay register posts it (payroll.post, from the provider); then the money
                it took from the bank is recorded here */}
            {d.status === 'sent' && (
              <span className="bp-pay-note">
                Sent: it posts when the provider’s pay register comes in, or when it’s entered here. Then
                record what the provider took from the bank.
              </span>
            )}
            {d.status === 'posted' && d.funding && (
              <span className="bp-pay-small" data-testid="payroll-funded">
                {fundingWords(d.funding)}
              </span>
            )}
            {d.status === 'posted' && !d.funding && (
              <RecordFunding
                period={d}
                {...(timeZone ? { timeZone } : {})}
                toast={toast}
                onDone={() => {
                  onFunded();
                  void p.reload();
                }}
              />
            )}
          </div>
        </div>
      </Section>
      <PeriodCommissions
        periodId={id}
        open={d.status === 'open'}
        toast={toast}
        version={version}
        onChanged={() => void p.reload()}
      />
      {(d.status === 'locked' || d.status === 'sent') && d.lines.length > 0 && (
        <Section title="The provider’s register">
          <EnterRegister key={d.id} period={d} toast={toast} onDone={() => void p.reload()} />
        </Section>
      )}
    </>
  );
}

// ── a period's commissions ──

function PeriodCommissions({
  periodId,
  open,
  toast,
  version,
  onChanged,
}: {
  periodId: string;
  open: boolean;
  toast: (m: string) => void;
  version: number;
  onChanged: () => void;
}) {
  const list = useQuery<{
    lines: { staffId: string; amountCents?: number }[];
    totals: { count: number; amountCents: number };
  }>('commissions.list', { periodId }, [version]);
  const staff = useQuery<{ staff: Staff[] }>('staff.list', {});
  const act = useAct(toast, async () => {
    await list.reload();
    onChanged();
  });
  const names = new Map((staff.data?.staff ?? []).map((s) => [s.id, s.displayName]));
  const rows = list.data ? commissionsByPerson(list.data.lines, names) : [];
  return (
    <Section title="Commissions">
      <div className="bp-pay-period">
        <p className="bp-pay-total">
          <span className="bp-pay-say">
            {list.data
              ? list.data.totals.count
                ? `${list.data.totals.count} in this period`
                : 'None locked into this period yet.'
              : ''}
          </span>
          {list.data && list.data.totals.count > 0 && <b>{money(list.data.totals.amountCents)}</b>}
        </p>
        {rows.length > 0 && rows.some((r) => r.amountCents !== null) && (
          <RowList
            label="Commissions by person"
            rows={rows.map((r) => ({
              id: r.staffId,
              name: r.name,
              lines: [{ text: `${r.count} job${r.count === 1 ? '' : 's'}` }],
              ...(r.amountCents !== null ? { end: { figure: money(r.amountCents) } } : {}),
            }))}
          />
        )}
        {open && (
          <div className="bp-pay-acts">
            <Link
              isDisabled={act.busy}
              onPress={() =>
                void act
                  .run<{ locked: number }>('commissions.lock', { periodId })
                  .then(
                    (r) =>
                      r &&
                      toast(
                        r.locked
                          ? `${r.locked} commission${r.locked === 1 ? '' : 's'} locked into this period.`
                          : 'Nothing new to lock: every completed job’s commission is already in.',
                      ),
                  )
              }
            >
              Lock commissions
            </Link>
          </div>
        )}
      </div>
    </Section>
  );
}

// ── what the provider took from the bank ──

function RecordFunding({
  period,
  timeZone,
  toast,
  onDone,
}: {
  period: PayPeriodSummary;
  timeZone?: string;
  toast: (m: string) => void;
  onDone: () => void;
}) {
  const banks = useQuery<{ bankAccounts: BankAccount[] }>('bankAccounts.list', {});
  const accounts = useQuery<{
    accounts: { id: string; code: string; name: string; type: string; active: boolean }[];
  }>('accounts.list', { includeInactive: false });
  const [sheet, setSheet] = useState(false);
  // Who sends CRA the deductions and employer costs: the provider (in the same pull) or the company.
  const [remitter, setRemitter] = useState<'provider' | 'company'>('provider');
  const [amount, setAmount] = useState('');
  const [fee, setFee] = useState('');
  const [feeAccountId, setFeeAccountId] = useState('');
  const [fundedOn, setFundedOn] = useState(todayIn(timeZone));
  const [bankId, setBankId] = useState('');
  const cents = parseMoney(amount);
  const feeCents = fee.trim() ? parseMoney(fee) : 0;
  const expenses = (accounts.data?.accounts ?? []).filter((a) => a.type === 'expense' && a.active);
  const feeAccount = expenses.find((a) => a.id === feeAccountId) ?? null;
  const bank = banks.data?.bankAccounts.find((b) => b.id === (bankId || banks.data?.bankAccounts[0]?.id));
  const ready =
    cents !== null &&
    cents > 0 &&
    !!fundedOn &&
    !!bank &&
    feeCents !== null &&
    feeCents >= 0 &&
    feeCents < cents &&
    (feeCents === 0 || !!feeAccount);
  const what =
    remitter === 'provider'
      ? 'net pay, and the deductions and employer costs it sends CRA'
      : 'net pay only; the deductions and employer costs are the company’s to send CRA';
  return (
    <>
      <Button variant="primary" onPress={() => setSheet(true)}>
        Record the funding
      </Button>
      <FormDialog
        isOpen={sheet}
        onClose={() => setSheet(false)}
        title="Record the payroll funding"
        submitLabel={`Record ${cad(cents ?? 0)}`}
        isSubmitDisabled={!ready}
        // as the money sheet did: closing asks nothing
        isDirty={false}
        note={
          <>
            The provider took {cad(cents ?? 0)} from {bank?.name ?? 'the bank'} on {dayText(fundedOn)} for{' '}
            {dayText(period.startDate)} to {dayText(period.endDate)}: {what}
            {feeCents ? `, and its fee of ${cad(feeCents)}` : ''}.{' '}
            {remitter === 'provider'
              ? 'The payroll liabilities are cleared.'
              : 'The deductions and employer costs stay owing until the CRA payment is recorded.'}
          </>
        }
        onSubmit={async () => {
          if (!bank || cents === null || feeCents === null) return;
          // straight to the server, so a refusal (it names the amounts it expects) shows in the dialog
          try {
            const r = await client.call<{ remittedBy: 'provider' | 'company'; toRemitCents: number }>(
              'payroll.recordFunding',
              {
                periodId: period.id,
                fundedOn,
                amountCents: cents,
                bankAccountId: bank.id,
                includesRemittances: remitter === 'provider',
                ...(feeCents && feeAccount ? { fee: { cents: feeCents, accountId: feeAccount.id } } : {}),
              },
            );
            toast(
              r.remittedBy === 'company' && r.toRemitCents > 0
                ? `The funding is recorded. ${cad(r.toRemitCents)} is still to send CRA.`
                : 'The funding is recorded.',
            );
            onDone();
          } catch (e) {
            throw new Error(errorText(e), { cause: e });
          }
        }}
      >
        <ChoiceGroup
          selectionMode="single"
          label="Who sends CRA the deductions?"
          value={remitter}
          onChange={(v) => setRemitter(v === 'company' ? 'company' : 'provider')}
        >
          <Choice value="provider">The provider, in the same pull</Choice>
          <Choice value="company">We do: the provider took net pay only</Choice>
        </ChoiceGroup>
        <TextField
          label="What the provider took"
          description="The total on the bank statement, with any fee."
          inputMode="decimal"
          value={amount}
          onChange={setAmount}
          placeholder="0.00"
        />
        <TextField
          label="Its fee"
          description="Optional: the part of it that was the provider’s fee."
          inputMode="decimal"
          value={fee}
          onChange={setFee}
          placeholder="0.00"
        />
        {!!feeCents && (
          <SelectField
            label="Fee goes to"
            value={feeAccountId}
            onChange={setFeeAccountId}
            options={[
              { value: '', label: 'Pick an expense account…' },
              ...expenses.map((a) => ({ value: a.id, label: `${a.code} ${a.name}` })),
            ]}
          />
        )}
        <TextField type="date" label="Taken on" value={fundedOn} onChange={setFundedOn} />
        {(banks.data?.bankAccounts.length ?? 0) > 1 && (
          <SelectField
            label="From"
            value={bank?.id ?? ''}
            onChange={setBankId}
            options={banks.data!.bankAccounts.map((b) => ({ value: b.id, label: b.name }))}
          />
        )}
      </FormDialog>
    </>
  );
}

// ── what the company still owes CRA (runs it remits itself) ──

interface ToRemit {
  runs: { periodId: string; startDate: string; endDate: string; payDate: string; owingCents: number }[];
  owingCents: number;
}

/**
 * The deductions and employer costs of runs the company remits itself (payroll.toRemit), oldest
 * first, and "Record a CRA payment" (payroll.recordRemittance): one payment can cover several runs,
 * and a part payment clears the oldest first. Hidden when nothing is owed.
 */
function StillToRemit({
  timeZone,
  toast,
  version,
}: {
  timeZone?: string;
  toast: (m: string) => void;
  version: number;
}) {
  const owed = useQuery<ToRemit>('payroll.toRemit', {}, [version]);
  const banks = useQuery<{ bankAccounts: BankAccount[] }>('bankAccounts.list', {});
  const [sheet, setSheet] = useState(false);
  const [amount, setAmount] = useState('');
  const [paidOn, setPaidOn] = useState(todayIn(timeZone));
  const [paidTo, setPaidTo] = useState<'cra' | 'provider'>('cra');
  const [reference, setReference] = useState('');
  const [bankId, setBankId] = useState('');
  // held while the dialog is the same, so a retry after a lost answer records the payment once
  const save = useSaveKey(JSON.stringify([amount, paidOn, paidTo, reference, bankId]));
  if (!owed.data || owed.data.owingCents <= 0) return null;
  const total = owed.data.owingCents;
  const runs = owed.data.runs;
  const cents = amount.trim() ? parseMoney(amount) : total;
  const bank = banks.data?.bankAccounts.find((b) => b.id === (bankId || banks.data?.bankAccounts[0]?.id));
  const ready = cents !== null && cents > 0 && cents <= total && !!paidOn && !!bank;
  return (
    // what's owed is where it stands, waiting on a payment, at the end of the name's line as before
    <Section title="Still to send CRA" state={{ mark: 'waiting', word: money(total) }}>
      <div className="bp-pay-period">
        <RowList
          label="Runs still to send CRA"
          rows={runs.map((r) => ({
            id: r.periodId,
            name: dates(r),
            lines: [{ text: `Paid ${dayText(r.payDate)} · deductions and employer costs` }],
            end: { figure: money(r.owingCents) },
          }))}
        />
        <div className="bp-pay-acts">
          <Button variant="primary" onPress={() => setSheet(true)}>
            Record a CRA payment
          </Button>
        </div>
      </div>
      <FormDialog
        isOpen={sheet}
        onClose={() => setSheet(false)}
        title="Record the CRA payment"
        submitLabel={`Record ${cad(cents ?? 0)}`}
        isSubmitDisabled={!ready}
        isDirty={false}
        note={
          <>
            {cad(cents ?? 0)} left {bank?.name ?? 'the bank'} on {dayText(paidOn)} for the payroll deductions
            and employer costs{paidTo === 'provider' ? ', through the provider' : ' to CRA'}. It pays the
            oldest run first
            {cents !== null && cents < total ? `; ${cad(total - cents)} stays owing` : ''}.
          </>
        }
        onSubmit={async () => {
          if (!bank || cents === null) return;
          try {
            const r = await client.call<{ stillToRemitCents: number }>('payroll.recordRemittance', {
              remittanceId: save.key,
              periodIds: runs.map((x) => x.periodId),
              amountCents: cents,
              paidOn,
              bankAccountId: bank.id,
              paidTo,
              ...(reference.trim() ? { reference: reference.trim() } : {}),
            });
            setAmount('');
            setReference('');
            save.renew();
            toast(
              r.stillToRemitCents > 0
                ? `Recorded. ${cad(r.stillToRemitCents)} is still to send CRA.`
                : 'Recorded: nothing is owed to CRA for payroll.',
            );
            void owed.reload();
          } catch (e) {
            throw new Error(errorText(e), { cause: e });
          }
        }}
      >
        <TextField
          label="Amount"
          description={`Up to ${cad(total)}.`}
          inputMode="decimal"
          value={amount}
          onChange={setAmount}
          placeholder={(total / 100).toFixed(2)}
        />
        <TextField type="date" label="Paid on" value={paidOn} onChange={setPaidOn} />
        <SelectField
          label="Paid"
          value={paidTo}
          onChange={(v) => setPaidTo(v === 'provider' ? 'provider' : 'cra')}
          options={[
            { value: 'cra', label: 'To CRA' },
            { value: 'provider', label: 'Through the payroll provider' },
          ]}
        />
        <TextField
          label="Reference"
          value={reference}
          onChange={setReference}
          placeholder="Optional: the CRA confirmation"
        />
        {(banks.data?.bankAccounts.length ?? 0) > 1 && (
          <SelectField
            label="From"
            value={bank?.id ?? ''}
            onChange={setBankId}
            options={banks.data!.bankAccounts.map((b) => ({ value: b.id, label: b.name }))}
          />
        )}
      </FormDialog>
    </Section>
  );
}

// ── the provider's pay register, entered by hand (a company without a payroll integration) ──

interface RegisterRow {
  gross: string;
  deductions: string;
  employer: string;
}

/** A row of the register: a person's typed line, or (the foot) the sums. */
interface RegisterLine {
  id: string;
  name?: string;
  gross: number | null;
  deductions: number | null;
  employer: number | null;
  net: number | null;
}

/**
 * After the run, the provider's register (gross, deductions, net, employer costs per person) is typed
 * in and posted (payroll.postRegister), so the period posts and its funding can be recorded. Each
 * person's gross must match the period's; net is worked out as gross less deductions, as the
 * register shows it. Every mismatch comes back from the server in one sentence.
 */
function EnterRegister({
  period,
  toast,
  onDone,
}: {
  period: PayPeriod;
  toast: (m: string) => void;
  onDone: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [reference, setReference] = useState('');
  const [rows, setRows] = useState<Record<string, RegisterRow>>(() =>
    Object.fromEntries(
      period.lines.map((l) => [
        l.staffId,
        {
          gross: l.grossCents !== undefined ? (l.grossCents / 100).toFixed(2) : '',
          deductions: '',
          employer: '',
        },
      ]),
    ),
  );
  const [confirm, setConfirm] = useState(false);
  if (!open) return <Link onPress={() => setOpen(true)}>Enter the provider’s register</Link>;
  const cents = (t: string) => (t.trim() ? parseMoney(t) : 0);
  const parsed: RegisterLine[] = period.lines.map((l) => {
    const r = rows[l.staffId]!;
    const gross = r.gross.trim() ? parseMoney(r.gross) : null;
    const deductions = cents(r.deductions);
    const employer = cents(r.employer);
    const net = gross !== null && deductions !== null ? gross - deductions : null;
    return { id: l.staffId, name: l.name, gross, deductions, employer, net };
  });
  const ok =
    !!reference.trim() &&
    parsed.every(
      (p) =>
        p.gross !== null &&
        p.gross >= 0 &&
        p.deductions !== null &&
        p.deductions >= 0 &&
        p.employer !== null &&
        p.employer >= 0 &&
        p.net !== null &&
        p.net >= 0,
    );
  const sum = (k: 'gross' | 'deductions' | 'employer' | 'net') => parsed.reduce((t, p) => t + (p[k] ?? 0), 0);
  const set = (staffId: string, patch: Partial<RegisterRow>) =>
    setRows({ ...rows, [staffId]: { ...rows[staffId]!, ...patch } });
  const typed = (k: keyof RegisterRow, label: string, placeholder?: string): DataColumn<RegisterLine> => ({
    id: k,
    label,
    align: 'end',
    kind: 'control',
    fit: 'figure',
    cell: (r) =>
      r.name
        ? {
            control: {
              label: `${r.name}’s ${label.toLowerCase()}`,
              value: rows[r.id]![k],
              onChange: (v) => set(r.id, { [k]: v }),
              inputMode: 'decimal',
              ...(placeholder ? { placeholder } : {}),
            },
          }
        : { text: money(sum(k)) },
  });
  const columns: DataColumn<RegisterLine>[] = [
    { id: 'person', label: 'Person', rowHeader: true, cell: (r) => ({ text: r.name ?? 'Total' }) },
    typed('gross', 'Gross'),
    typed('deductions', 'Deductions', '0.00'),
    {
      id: 'net',
      label: 'Net',
      align: 'end',
      cell: (r) => ({ text: r.name ? (r.net !== null ? money(r.net) : '—') : money(sum('net')) }),
    },
    typed('employer', 'Employer costs', '0.00'),
  ];
  const post = async () => {
    try {
      await client.call('payroll.postRegister', {
        periodId: period.id,
        reference: reference.trim(),
        lines: parsed.map((p) => ({
          staffId: p.id,
          grossCents: p.gross!,
          deductionsCents: p.deductions!,
          netCents: p.net!,
          employerCostsCents: p.employer!,
        })),
      });
    } catch (e) {
      throw new Error(errorText(e), { cause: e });
    }
    setOpen(false);
    toast('The register is posted: the period is posted.');
    onDone();
  };
  return (
    <>
      <form
        className="bp-pay-register"
        aria-label="The provider’s register"
        onSubmit={(e) => {
          e.preventDefault();
          if (ok) setConfirm(true);
        }}
      >
        <p className="bp-pay-say">
          Type each person’s line from the provider’s pay register. Gross must match this period; net is gross
          less deductions.
        </p>
        <div className="bp-pay-ref">
          <TextField label="Provider’s run number" width="short" value={reference} onChange={setReference} />
        </div>
        <DataTable
          label="Pay register"
          columns={columns}
          rows={parsed}
          foot={{ id: 'total', gross: null, deductions: null, employer: null, net: null }}
          empty="No one was paid hours in this period."
        />
        <div className="bp-pay-acts">
          <Button variant="primary" type="submit" isDisabled={!ok}>
            Post the register
          </Button>
          <Link onPress={() => setOpen(false)}>Cancel</Link>
        </div>
      </form>
      <ConfirmDialog
        isOpen={confirm}
        onClose={() => setConfirm(false)}
        title="Post the pay register"
        confirmLabel={`Post ${cad(sum('gross'))}`}
        cancelLabel="Cancel"
        onConfirm={post}
      >
        Wages of {cad(sum('gross'))} and employer costs of {cad(sum('employer'))} post for{' '}
        {dayText(period.startDate)} to {dayText(period.endDate)}, against payroll liabilities; net pay of{' '}
        {cad(sum('net'))} is what the provider pays out. The period’s commissions are marked paid.
      </ConfirmDialog>
    </>
  );
}

// ── each person's pay and commission rates (the owner's) ──

function PayAndCommissions({ toast }: { toast: (m: string) => void }) {
  const staff = useQuery<{ staff: Staff[] }>('staff.list', {});
  const [staffId, setStaffId] = useState('');
  const person = staff.data?.staff.find((s) => s.id === staffId);
  return (
    <Section title="Pay and commission rates">
      {!staff.data ? (
        <Waiting error={staff.error} what="the team" onRetry={() => void staff.reload()} />
      ) : staff.data.staff.length === 0 ? (
        <p className="bp-pay-say">No one on the team yet.</p>
      ) : (
        <div className="bp-pay-period">
          <div className="bp-pay-ref">
            <SelectField
              label="Person"
              width="fit"
              value={staffId}
              onChange={setStaffId}
              options={[
                { value: '', label: 'Pick someone…' },
                ...staff.data.staff.map((s) => ({ value: s.id, label: s.displayName })),
              ]}
            />
          </div>
          {person && (
            <>
              <p className="bp-pay-say">Saving replaces what’s set now for {person.displayName}.</p>
              <PayForm key={`pay-${person.id}`} person={person} toast={toast} />
              <PlanForm key={`plan-${person.id}`} person={person} toast={toast} />
            </>
          )}
        </div>
      )}
    </Section>
  );
}

function PayForm({ person, toast }: { person: Staff; toast: (m: string) => void }) {
  const [kind, setKind] = useState<'field' | 'office'>(person.isTechnician ? 'field' : 'office');
  const [payType, setPayType] = useState<'hourly' | 'salary'>('hourly');
  const [amount, setAmount] = useState('');
  const [providerId, setProviderId] = useState('');
  const [since, setSince] = useState('');
  const act = useAct(toast, () => undefined);
  const cents = parseMoney(amount);
  const ok = cents !== null && cents >= 0;
  return (
    <form
      className="bp-pay-form"
      aria-label={`${person.displayName}’s pay`}
      onSubmit={(e) => {
        e.preventDefault();
        if (!ok || act.busy) return;
        void act.run(
          'payroll.setPay',
          {
            staffId: person.id,
            kind,
            payType,
            ...(payType === 'hourly' ? { hourlyRateCents: cents } : { salaryPeriodCents: cents }),
            ...(providerId.trim() ? { providerEmployeeId: providerId.trim() } : {}),
            ...(since ? { employedSince: since } : {}),
          },
          `${person.displayName}’s pay is saved.`,
        );
      }}
    >
      <h3 className="bp-pay-sub">Pay</h3>
      <SelectField
        label="Works"
        width="fit"
        value={kind}
        onChange={(v) => setKind(v === 'office' ? 'office' : 'field')}
        options={[
          { value: 'field', label: 'In the field' },
          { value: 'office', label: 'In the office' },
        ]}
      />
      <SelectField
        label="Paid"
        width="fit"
        value={payType}
        onChange={(v) => setPayType(v === 'salary' ? 'salary' : 'hourly')}
        options={[
          { value: 'hourly', label: 'By the hour' },
          { value: 'salary', label: 'A salary' },
        ]}
      />
      <TextField
        label={payType === 'hourly' ? 'Rate per hour' : 'Salary per pay period'}
        width="short"
        inputMode="decimal"
        value={amount}
        onChange={setAmount}
        placeholder="0.00"
      />
      <TextField
        label="Their id at the payroll provider"
        width="short"
        value={providerId}
        onChange={setProviderId}
        placeholder="Optional"
      />
      <TextField
        type="date"
        width="date"
        label="First day (for stat and sick pay)"
        value={since}
        onChange={setSince}
      />
      <div className="bp-pay-acts">
        <Button variant="primary" type="submit" isDisabled={!ok || act.busy}>
          {act.busy ? 'Saving…' : 'Save the pay'}
        </Button>
      </div>
    </form>
  );
}

function PlanForm({ person, toast }: { person: Staff; toast: (m: string) => void }) {
  const [rates, setRates] = useState<Record<string, string>>({});
  const act = useAct(toast, () => undefined);
  const parsed = COMMISSION_RULES.map(([k]) => [k, percentToRate(rates[k] ?? '')] as const);
  const ok = parsed.every(([, r]) => r !== null);
  return (
    <form
      className="bp-pay-form"
      aria-label={`${person.displayName}’s commission`}
      onSubmit={(e) => {
        e.preventDefault();
        if (!ok || act.busy) return;
        void act.run(
          'commissions.setPlan',
          { staffId: person.id, ...Object.fromEntries(parsed) },
          `${person.displayName}’s commission rates are saved.`,
        );
      }}
    >
      <h3 className="bp-pay-sub">Commission (% of the work)</h3>
      {COMMISSION_RULES.map(([k, label]) => (
        <TextField
          key={k}
          label={label}
          width="number"
          inputMode="decimal"
          value={rates[k] ?? ''}
          onChange={(v) => setRates({ ...rates, [k]: v })}
          placeholder="0"
          isInvalid={percentToRate(rates[k] ?? '') === null}
        />
      ))}
      <div className="bp-pay-acts">
        <Button variant="primary" type="submit" isDisabled={!ok || act.busy}>
          {act.busy ? 'Saving…' : 'Save the commission rates'}
        </Button>
      </div>
    </form>
  );
}

// ── the page ──

export function PayrollPage({
  nav,
  held,
  toast,
  timeZone,
  focus,
  onHours,
}: {
  nav: NavProps;
  held: readonly string[];
  toast: (m: string) => void;
  timeZone?: string;
  /** A pay period a link opened (Overview's books check, say). */
  focus?: string;
  /** The period's timesheets: Hours, on the period. */
  onHours?: (date: string) => void;
}) {
  const list = useQuery<{ periods: PayPeriodSummary[] }>('payPeriods.list', {});
  const [chosen, setChosen] = useState<string | null>(focus ?? null);
  const [funded, setFunded] = useState(0);
  // the owner reviews and sends it (payroll.send: the owner's, or a role of theirs that gives it)
  const owner = held.includes('payroll.send');
  const crumb = usePageCrumb(nav);
  const periods = list.data?.periods ?? [];
  const id = chosen ?? periods[0]?.id ?? null;
  const shown = periods.find((x) => x.id === id) ?? null;
  const newest = periods[0] ?? null;
  // a period chosen from elsewhere that's older than the six still shows as chosen
  const picks =
    shown && !periods.slice(0, SHOWN).includes(shown)
      ? [...periods.slice(0, SHOWN), shown]
      : periods.slice(0, SHOWN);
  const opened = (periodId: string) => {
    setChosen(periodId);
    void list.reload();
  };
  return (
    <div className="app">
      <main>
        <PageBanners nav={nav} />
        <PageHead {...(crumb ? { crumb } : {})} title={PART.bp ?? PART.rail} lede={PART.lede} />
        <div className="bp-pay">
          {!list.data ? (
            <Waiting error={list.error} what="the pay periods" onRetry={() => void list.reload()} />
          ) : (
            <>
              {(periods.length > 1 || newest) && (
                <div className="bp-pay-tools">
                  {periods.length > 1 && (
                    <SelectField
                      label="Pay period"
                      width="fit"
                      value={id ?? ''}
                      onChange={setChosen}
                      options={picks.map((x) => ({ value: x.id, label: dates(x) }))}
                    />
                  )}
                  {newest && (
                    <OpenPeriod
                      key={newest.id}
                      newest={newest}
                      {...(timeZone ? { timeZone } : {})}
                      toast={toast}
                      onOpened={opened}
                      quiet
                    />
                  )}
                </div>
              )}
              {id ? (
                <PeriodView
                  key={id}
                  id={id}
                  summary={shown}
                  owner={owner}
                  {...(timeZone ? { timeZone } : {})}
                  toast={toast}
                  onFunded={() => setFunded((n) => n + 1)}
                />
              ) : (
                <OpenPeriod
                  newest={null}
                  {...(timeZone ? { timeZone } : {})}
                  toast={toast}
                  onOpened={opened}
                />
              )}
              <StillToRemit {...(timeZone ? { timeZone } : {})} toast={toast} version={funded} />
              {shown && onHours && (
                // Blueprint has one timesheet (timesheet rules §4, §5): the period's, on Hours
                <Section title="Timesheet">
                  <p className="bp-pay-say">
                    The technicians’ hours for {dates(shown)} are fixed and locked on Hours, a timesheet per
                    person.
                  </p>
                  <p className="bp-pay-line">
                    <Link onPress={() => onHours(shown.startDate)}>Open the timesheets ›</Link>
                  </p>
                </Section>
              )}
              {owner && <PayAndCommissions toast={toast} />}
            </>
          )}
        </div>
      </main>
    </div>
  );
}
